import { useEffect } from "react";
import mapboxgl from "mapbox-gl";
import { mapActive, mapUtils } from "../../../map/utils/map-utils";
import { Feature, FeatureCollection, LineString } from "geojson";
import turf from "../../../shared/utils/turf";
import { useStores } from "../../../shared/stores/RootStore";
import { OsmData } from "../../../map/models/OsmData";

const polygonSourceName = "polygon";
const dataSourceName = "data";
const selectedColor = "#c320e0";

export const useOsmToolsMap = () => {
  const { osmToolsStore, streetViewStore } = useStores();
  const { polygons, data, map, selectedWay, highlightedTag, cityBBox } =
    osmToolsStore;

  useEffect(() => {
    const map = new mapboxgl.Map({
      ...mapUtils.defaultConfig,
    });

    map.on("load", () => {
      map.boxZoom.disable();
      map.addControl(
        new mapboxgl.ScaleControl({
          maxWidth: 100,
          unit: "metric",
        }),
      );

      osmToolsStore.registerMap(map);
    });

    map.once("style.load", () => {
      map.resize();

      map.addSource(polygonSourceName, {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: [],
        },
      });

      map.addLayer({
        source: polygonSourceName,
        id: "poligon-line",
        type: "line",
        layout: {
          "line-join": "round",
          "line-cap": "round",
        },
        paint: {
          "line-color": ["get", "color"],
          "line-width": 4,
        },
      });

      map.addLayer({
        source: polygonSourceName,
        id: "poligon-fill",
        type: "fill",
        paint: {
          "fill-color": ["get", "color"],
          "fill-opacity": 0.2,
        },
      });

      map.addSource(dataSourceName, {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: [],
        },
      });

      map.addLayer({
        source: dataSourceName,
        id: "data-stroke",
        type: "line",
        layout: {
          "line-join": "round",
          "line-cap": "round",
        },
        paint: {
          "line-color": "#1d1d1d",
          "line-width": 7,
        },
      });

      map.addLayer({
        source: dataSourceName,
        id: "data-line",
        type: "line",
        layout: {
          "line-join": "round",
          "line-cap": "round",
        },
        paint: {
          "line-color": [
            "case",
            ["==", ["get", "selected"], "yes"],
            "#64de8d",
            "#ffffff",
          ],
          "line-width": 5,
        },
      });

      osmToolsStore.initStreetViewSource(map);

      map.addInteraction("line-click", {
        type: "click",
        target: {
          layerId: "data-stroke",
        },
        handler: (e) => {
          if (!e.feature) return;

          const wayId = e.feature.id as number;

          osmToolsStore.selectWay(wayId);

          streetViewStore?.goToLineString(e.feature.geometry as LineString);
        },
      });

      const canvas = map.getCanvasContainer();

      map.addInteraction("line-enter", {
        type: "mouseenter",
        target: {
          layerId: "data-stroke",
        },
        handler: (e) => {
          canvas.style.cursor = "pointer";
        },
      });

      map.addInteraction("line-leave", {
        type: "mouseleave",
        target: {
          layerId: "data-stroke",
        },
        handler: (e) => {
          canvas.style.cursor = "";
        },
      });
    });

    return () => {
      map?.remove();
    };
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (mapActive(map)) {
      const source = map!.getSource(
        polygonSourceName,
      ) as mapboxgl.GeoJSONSource;
      source?.setData({
        type: "FeatureCollection",
        features: polygons ?? [],
      });

      if (cityBBox) {
        const bounds = new mapboxgl.LngLatBounds();

        bounds.extend([cityBBox[0], cityBBox[1]]);
        bounds.extend([cityBBox[2], cityBBox[3]]);

        map!.fitBounds(bounds, {
          padding: 50,
        });
      }
    }
    // eslint-disable-next-line
  }, [polygons]);

  useEffect(() => {
    if (mapActive(map) && data) {
      const bounds = new mapboxgl.LngLatBounds();

      const featCol = osmDataToFeatureCollection(
        data,
        { selectedWayId: selectedWay?.id },
        bounds,
      );

      const source = map!.getSource(dataSourceName) as mapboxgl.GeoJSONSource;
      source?.setData(featCol);

      if (!selectedWay && !bounds.isEmpty()) {
        map!.fitBounds(bounds, {
          padding: 50,
        });
      }
    }
    // eslint-disable-next-line
  }, [data, map, selectedWay]);

  useEffect(() => {
    if (!mapActive(map)) return;

    const layerName = "highlighted-tag";

    if (map!.getLayer(layerName)) {
      map!.removeLayer(layerName);
    }

    if (highlightedTag) {
      map!.addLayer({
        source: dataSourceName,
        id: layerName,
        type: "line",
        layout: {
          "line-join": "round",
          "line-cap": "round",
        },
        paint: {
          "line-color": selectedColor,
          "line-width": 3,
        },
        filter: ["==", highlightedTag.key, highlightedTag.value],
      });
    }

    // eslint-disable-next-line
  }, [highlightedTag, map]);

  return map;
};

export const osmDataToFeatureCollection = (
  data: OsmData,
  options?: { selectedWayId?: number },
  bounds?: mapboxgl.LngLatBounds,
): FeatureCollection => {
  const { selectedWayId } = options ?? {};
  const nodesById = Object.groupBy(data.nodes, ({ id }) => id);

  const features: Feature<LineString>[] = data.ways.flatMap((way) => {
    const tags = { ...way.tags };
    if (way.id === selectedWayId) {
      tags.selected = "yes";
    }

    const coordinates = way.nodes.flatMap((nodeId) => {
      const node = nodesById[nodeId]?.[0];

      if (node) {
        bounds?.extend(node);
        return [[node.lng, node.lat]];
      } else {
        return [];
      }
    });

    if (coordinates.length < 2) {
      return [];
    }

    return [turf.lineString(coordinates, tags, { id: way.id })];
  });

  return {
    type: "FeatureCollection",
    features: features,
  };
};
